<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { TimelineRulerProps } from "./timeline-ruler";
import type { TickCfg } from "./timeline-ruler";

defineOptions({
  name: "ITimelineRuler",
});

const props = defineProps(TimelineRulerProps);

const emit = defineEmits(["seek"]);

// ==================== DOM Refs ====================
const canvasRef = ref<HTMLCanvasElement | null>(null);
const rulerAreaRef = ref<HTMLElement | null>(null);
const recTrackRef = ref<HTMLElement | null>(null);

// ==================== UI State ====================
const timeBigHtml = ref(
  '--<span class="sep">:</span>--<span class="sep">:</span>--',
);
const timeDateText = ref("----/--/--");
const tipShow = ref(false);
const tipText = ref("");
/** ruler tooltip DOM 代理引用，延迟到 onMounted 赋值 */
let tipElProxy: Element | null = null;
const tipStyle = ref<Record<string, string>>({});
const zoomTagText = ref("");

// ==================== Ruler Constants ====================
const RH = 56;
const BASE_Y = 44;
const FRIC = 0.9;

// ==================== Ruler State ====================
let BASE_TIME: number | null = null;
let pph = 120;
const tPph = 120;
let offX = 0;
let dragging = false;
let dragSX = 0;
let dragSO = 0;
let vel = 0;
let lpX = 0;
let lpT = 0;
let mX = -1;
let mIn = false;
let _startTime: Date | null = null;
let _durationSec = 3600;
let _currentPlayMs: number | null = null;

// Canvas refs
let cvs: HTMLCanvasElement | null = null;
let cx: CanvasRenderingContext2D | null = null;
let dpr = 1;
let rafId: number | null = null;

// ==================== Utils ====================
function pad(n: number) {
  return String(n).padStart(2, "0");
}

function fmtT(s: number) {
  const d = new Date(s * 1000);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fmtTF(s: number) {
  const d = new Date(s * 1000);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
function fmtD(s: number) {
  const d = new Date(s * 1000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dateToSec(dt: Date): number {
  return dt.getTime() / 1000;
}

// ==================== Coord Transform ====================
function t2x(t: number): number {
  return ((t - BASE_TIME!) * pph) / 3600 + offX;
}
function x2t(x: number): number {
  return ((x - offX) * 3600) / pph + BASE_TIME!;
}

// ==================== Resize ====================
function resize() {
  if (!cvs || !rulerAreaRef.value) return;
  const ow = cvs!.width / dpr || 0;
  const r = rulerAreaRef.value.getBoundingClientRect();
  cvs!.width = r.width * dpr;
  cvs!.height = RH * dpr;
  (cvs as HTMLCanvasElement).style.height = `${RH}px`;
  cx!.setTransform(dpr, 0, 0, dpr, 0, 0);
  if (ow > 0) {
    const ct = x2t(ow / 2);
    offX = r.width / 2 - ((ct - BASE_TIME!) * pph) / 3600;
  }
}

// ==================== Tick Config ====================
function tCfg(): Record<string, TickCfg> {
  if (pph >= 300)
    return {
      M: { iv: 3600, h: 32, w: 1.5, c: "#8899b4", lbl: true },
      m: { iv: 1800, h: 22, w: 1, c: "#4a5a74" },
      s: { iv: 600, h: 14, w: 0.7, c: "#33415a" },
      u: { iv: 300, h: 8, w: 0.5, c: "#252f42" },
      x: { iv: 60, h: 4, w: 0.3, c: "#1c2535" },
    };
  if (pph >= 140)
    return {
      M: { iv: 3600, h: 32, w: 1.5, c: "#8899b4", lbl: true },
      m: { iv: 1800, h: 22, w: 1, c: "#4a5a74" },
      s: { iv: 600, h: 14, w: 0.7, c: "#33415a" },
      u: { iv: 300, h: 8, w: 0.5, c: "#252f42" },
    };
  if (pph >= 70)
    return {
      M: { iv: 3600, h: 32, w: 1.5, c: "#8899b4", lbl: true },
      m: { iv: 1800, h: 20, w: 1, c: "#4a5a74" },
      s: { iv: 600, h: 13, w: 0.7, c: "#33415a" },
    };
  if (pph >= 35)
    return {
      M: { iv: 3600, h: 32, w: 1.5, c: "#8899b4", lbl: true },
      m: { iv: 1800, h: 18, w: 0.8, c: "#4a5a74" },
    };
  return {
    M: { iv: 7200, h: 32, w: 1.5, c: "#8899b4", lbl: true },
    m: { iv: 3600, h: 18, w: 0.8, c: "#4a5a74" },
  };
}

// ==================== Draw ====================
function draw() {
  if (!cvs || !cx) return;
  const w = cvs.width / dpr;
  const h = cvs.height / dpr;
  cx.clearRect(0, 0, w, h);

  // 基线
  cx.strokeStyle = "#2a3448";
  cx.lineWidth = 1;
  cx.beginPath();
  cx.moveTo(0, BASE_Y);
  cx.lineTo(w, BASE_Y);
  cx.stroke();

  // 多级刻度
  const cfg = tCfg();
  const lt = x2t(-100);
  const rt = x2t(w + 100);
  const order = ["x", "u", "s", "m", "M"];
  for (let li = 0; li < order.length; li++) {
    const lv = order[li];
    const tk = cfg[lv];
    if (!tk) continue;
    const st = Math.floor(lt / tk.iv) * tk.iv;
    for (let t = st; t <= rt + tk.iv; t += tk.iv) {
      const x = t2x(t);
      if (x < -20 || x > w + 20) continue;
      cx.strokeStyle = tk.c;
      cx.lineWidth = tk.w;
      cx.beginPath();
      cx.moveTo(x, BASE_Y);
      cx.lineTo(x, BASE_Y - tk.h);
      cx.stroke();
      if (tk.lbl) {
        cx.fillStyle = "#7a8da6";
        cx.font = '700 10px "Space Mono",monospace';
        cx.textAlign = "center";
        cx.textBaseline = "top";
        cx.fillText(fmtT(t), x, 4);
        const dd = new Date(t * 1000);
        if (dd.getHours() === 0 && dd.getMinutes() === 0) {
          cx.fillStyle = "#4a5a74";
          cx.font = '9px "Space Mono",monospace';
          cx.fillText(fmtD(t), x, -1);
          cx.save();
          cx.strokeStyle = "#1e2a3a";
          cx.lineWidth = 1;
          cx.setLineDash([3, 3]);
          cx.beginPath();
          cx.moveTo(x, 14);
          cx.lineTo(x, BASE_Y);
          cx.stroke();
          cx.restore();
        }
      }
    }
  }

  // 当前播放位置线（绿色）
  if (_currentPlayMs && _startTime) {
    const playSec = _currentPlayMs / 1000;
    const nx = t2x(playSec);
    if (nx > -20 && nx < w + 20) {
      cx.strokeStyle = "#3ddc84";
      cx.lineWidth = 1.5;
      cx.beginPath();
      cx.moveTo(nx, BASE_Y - 32);
      cx.lineTo(nx, BASE_Y);
      cx.stroke();
      cx.fillStyle = "#3ddc84";
      cx.beginPath();
      cx.moveTo(nx, BASE_Y + 1);
      cx.lineTo(nx - 3, BASE_Y + 6);
      cx.lineTo(nx + 3, BASE_Y + 6);
      cx.closePath();
      cx.fill();
    }
  }

  // 中心指针（红色）
  const px = w / 2;
  const g = cx.createLinearGradient(px - 20, 0, px + 20, 0);
  g.addColorStop(0, "rgba(255,45,45,0)");
  g.addColorStop(0.5, "rgba(255,45,45,0.06)");
  g.addColorStop(1, "rgba(255,45,45,0)");
  cx.fillStyle = g;
  cx.fillRect(px - 20, 0, 40, BASE_Y);

  cx.strokeStyle = "#ff2d2d";
  cx.lineWidth = 1.5;
  cx.beginPath();
  cx.moveTo(px, 0);
  cx.lineTo(px, BASE_Y);
  cx.stroke();

  cx.fillStyle = "#ff2d2d";
  cx.beginPath();
  cx.moveTo(px, 0);
  cx.lineTo(px - 4, 6);
  cx.lineTo(px + 4, 6);
  cx.closePath();
  cx.fill();

  cx.beginPath();
  cx.moveTo(px, BASE_Y + 1);
  cx.lineTo(px - 3, BASE_Y + 7);
  cx.lineTo(px + 3, BASE_Y + 7);
  cx.closePath();
  cx.fill();

  // 鼠标悬停线
  if (mIn && mX >= 0 && !dragging) {
    cx.save();
    cx.strokeStyle = "rgba(136,153,180,0.4)";
    cx.lineWidth = 1;
    cx.setLineDash([2, 2]);
    cx.beginPath();
    cx.moveTo(mX, 4);
    cx.lineTo(mX, BASE_Y);
    cx.stroke();
    cx.restore();
    cx.fillStyle = "#8899b4";
    cx.beginPath();
    cx.arc(mX, BASE_Y, 2.5, 0, Math.PI * 2);
    cx.fill();

    const ht = x2t(mX);
    tipText.value = `${fmtD(ht)} ${fmtTF(ht)}`;
    tipStyle.value = { left: `${mX + 14}px` };
    tipShow.value = true;
    requestAnimationFrame(() => {
      if (!tipElProxy || !rulerAreaRef.value) return;
      const tr = tipElProxy.getBoundingClientRect();
      const wr = rulerAreaRef.value.getBoundingClientRect();
      if (tr.right > wr.right - 6)
        tipStyle.value = { left: `${mX - tr.width - 14}px` };
    });
  } else {
    tipShow.value = false;
  }

  // 边缘渐隐
  const fw = 28;
  const lf = cx.createLinearGradient(0, 0, fw, 0);
  lf.addColorStop(0, "#1a2030");
  lf.addColorStop(1, "rgba(26,32,48,0)");
  cx.fillStyle = lf;
  cx.fillRect(0, 0, fw, h);
  const rf = cx.createLinearGradient(w - fw, 0, w, 0);
  rf.addColorStop(0, "rgba(26,32,48,0)");
  rf.addColorStop(1, "#1a2030");
  cx.fillStyle = rf;
  cx.fillRect(w - fw, 0, fw, h);

  // 更新中心时间显示
  const ct = x2t(px);
  const cd = new Date(ct * 1000);
  timeBigHtml.value = `${pad(cd.getHours())}<span class="sep">:</span>${pad(
    cd.getMinutes(),
  )}<span class="sep">:</span>${pad(cd.getSeconds())}`;
  timeDateText.value = fmtD(ct);

  // 录像段
  renderRecSegs(w);
}

// 录像段渲染
function renderRecSegs(w?: number) {
  if (!w) w = (cvs as HTMLCanvasElement).width / dpr;
  if (!recTrackRef.value) return;
  recTrackRef.value.innerHTML = "";
  if (!_startTime) return;

  const startS = dateToSec(_startTime);
  const endS = startS + _durationSec;
  const lt = x2t(-20);
  const rt = x2t(w + 20);

  if (endS < lt || startS > rt) return;

  const x1 = t2x(Math.max(startS, lt));
  const x2 = t2x(Math.min(endS, rt));
  if (x2 - x1 < 0.5) return;

  const div = document.createElement("div");
  div.className = "rec-seg";
  div.style.left = `${x1}px`;
  div.style.width = `${x2 - x1}px`;
  recTrackRef.value.appendChild(div);
}

// ==================== Animation Loop ====================
function loop() {
  // 惯性滑动
  if (!dragging && Math.abs(vel) > 0.05) {
    offX += vel;
    vel *= FRIC;
  } else if (!dragging) vel = 0;

  // 平滑缩放
  if (Math.abs(pph - tPph) > 0.2) pph += (tPph - pph) * 0.15;
  else pph = tPph;

  zoomTagText.value = `${Math.round(pph)} px/h`;

  draw();

  rafId = requestAnimationFrame(loop);
}

// ==================== Events ====================
function gx(e: MouseEvent | TouchEvent): number {
  if (!cvs) return 0;
  const r = cvs!.getBoundingClientRect();
  const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
  return clientX - r.left;
}

function onDown(e: MouseEvent | TouchEvent) {
  e.preventDefault();
  dragging = true;
  vel = 0;
  dragSX = gx(e);
  dragSO = offX;
  lpX = dragSX;
  lpT = performance.now();
  (cvs as HTMLCanvasElement).style.cursor = "grabbing";
}

function onMove(e: MouseEvent | TouchEvent) {
  if (!dragging) {
    if (e.target === cvs || (cvs && cvs!.contains(e.target as Node))) {
      if (!cvs) return;
      const r = cvs!.getBoundingClientRect();
      mX = ("touches" in e ? e.touches[0].clientX : e.clientX) - r.left;
      mIn = true;
    }
    return;
  }
  e.preventDefault();
  const x = gx(e);
  offX = dragSO + (x - dragSX);
  const now = performance.now();
  const dt = now - lpT;
  if (dt > 0) vel = ((x - lpX) / dt) * 16;
  lpX = x;
  lpT = now;
}

function onUp() {
  if (!dragging) return;
  dragging = false;
  (cvs as HTMLCanvasElement).style.cursor = mIn ? "grab" : "default";
  vel = 0;
  if (dragSO !== offX) {
    const lockedSec = x2t(cvs!.width / dpr / 2);
    emit("seek", lockedSec);
  }
}

// ==================== Public API ====================
/** 初始化/重置时间轴 */
function initRuler(startTimeDate: Date, durationSeconds?: number) {
  _startTime = startTimeDate;
  _durationSec = durationSeconds || 3600;
  BASE_TIME = dateToSec(startTimeDate);
  resize();
  const w = (cvs as HTMLCanvasElement).width / dpr;
  offX = w / 2 - ((BASE_TIME! - BASE_TIME!) * pph) / 3600;
  _currentPlayMs = startTimeDate.getTime();
}

/** 更新当前播放时间（由父组件定时调用） */
function updateRulerTime(timeMs: number) {
  _currentPlayMs = timeMs;
}

/** 播放时自动跟随指针到中心 */
function autoFollow() {
  if (!_currentPlayMs || dragging || !cvs) return;
  const targetX = t2x(_currentPlayMs / 1000);
  const w = (cvs as HTMLCanvasElement).width / dpr;
  const halfW = w / 2;
  if (Math.abs(targetX - halfW) > halfW * 0.5) {
    offX += (targetX - halfW) * 0.08;
  }
}

defineExpose({ initRuler, updateRulerTime, autoFollow });

// ==================== Lifecycle ====================
onMounted(() => {
  cvs = canvasRef.value;
  cx = cvs!.getContext("2d");
  dpr = window.devicePixelRatio || 1;

  // 绑定事件
  cvs!.addEventListener("mousedown", onDown);
  cvs!.addEventListener("touchstart", onDown, { passive: false });
  window.addEventListener("mousemove", onMove);
  window.addEventListener("touchmove", onMove, { passive: false });
  window.addEventListener("mouseup", onUp);
  window.addEventListener("touchend", onUp);
  cvs!.addEventListener("mouseenter", () => {
    mIn = true;
    (cvs as HTMLCanvasElement).style.cursor = "grab";
  });
  cvs!.addEventListener("mouseleave", () => {
    mIn = false;
    mX = -1;
    if (!dragging) (cvs as HTMLCanvasElement).style.cursor = "default";
  });

  // 点击跳转
  cvs!.addEventListener("click", (e) => {
    if (Math.abs(gx(e as MouseEvent)) - dragSX > 5) return;
    const clickX = gx(e as MouseEvent);
    const lockedSec = x2t(clickX);
    vel = 0;
    offX = clickX - cvs!.width / dpr / 2;
    emit("seek", lockedSec);
  });

  // resize
  window.addEventListener("resize", resize);

  // 获取 tip 代理元素
  nextTick(() => {
    tipElProxy = document.querySelector(".ruler-area .tip");
  });

  // 初始化
  if (props.startTime) {
    initRuler(props.startTime, props.duration);
  } else {
    BASE_TIME = Date.now() / 1000;
    resize();
    const w = cvs!.width / dpr;
    offX = w / 2;
  }
  loop();
});

onBeforeUnmount(() => {
  if (rafId) cancelAnimationFrame(rafId);
  window.removeEventListener("resize", resize);
  window.removeEventListener("mousemove", onMove);
  window.removeEventListener("touchmove", onMove);
  window.removeEventListener("mouseup", onUp);
  window.removeEventListener("touchend", onUp);
});
</script>

<template>
  <div class="ruler-panel">
    <div class="time-display">
      <div class="time-big" v-html="timeBigHtml" />
      <div class="time-date">{{ timeDateText }}</div>
    </div>

    <div ref="recTrackRef" class="rec-track" />

    <div ref="rulerAreaRef" class="ruler-area">
      <canvas id="rulerCanvas" ref="canvasRef" class="ruler-canvas" />
      <div class="tip" :class="{ show: tipShow }" :style="tipStyle">
        {{ tipText }}
      </div>
      <div class="zoom-tag">{{ zoomTagText }}</div>
    </div>
  </div>
</template>
